/**
 * useAuthSession — Authentication & global context state for the App shell.
 *
 * Owns:
 *   - isAuthenticated flag
 *   - globalContextData (resolved from PortalAuth)
 *   - login(token, ctxData, userSummary) — wired into PortalAuth onSuccess
 *   - logout() — clears tokens + GlobalStore
 */
import { useCallback, useState } from 'react';
import { API_BASE, getToken } from '@/lib/api';
import SessionService from '@/services/SessionService';

const PERSISTED_KEYS = [
  'retailcliq_token',
  'retailcliq_global_context',
  'rcq_access_token',
  'rcq_user',
  'rcq_global_context',
];

export default function useAuthSession({ onLoginReset } = {}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [globalContextData, setGlobalContextData] = useState(null);

  const login = useCallback((token, ctxData, userSummary) => {
    // ═══ FRESH SESSION INITIALIZATION ═══
    // Clear previous UI navigation state, workflow cache, temp screen data
    SessionService.initializeFreshWorkspace();

    if (typeof onLoginReset === 'function') onLoginReset();

    if (ctxData) {
      setGlobalContextData(ctxData);
    } else if (userSummary) {
      setGlobalContextData({
        userId: userSummary.client_id || userSummary.slno || userSummary.name,
        username: userSummary.name,
        role: userSummary.role,
        company: userSummary.company ? { compName: userSummary.company } : null,
      });
    }
    setIsAuthenticated(true);
  }, [onLoginReset]);

  const logout = useCallback(async () => {
    const token = getToken() || sessionStorage.getItem('retailcliq_token');
    try {
      if (token) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
      }
    } catch (_) { /* silent — best effort */ }

    PERSISTED_KEYS.forEach(k => {
      try { localStorage.removeItem(k); sessionStorage.removeItem(k); } catch (_) { /* noop */ }
    });
    try {
      const [storeMod, keysMod] = await Promise.all([
        import('@/store/GlobalStore'),
        import('@/store/GlobalKeys'),
      ]);
      const gs = storeMod.default;
      const GK = keysMod.GK;
      // Record exit time + session duration BEFORE clearing
      const loginTime = gs.get(GK.LOGIN_TIME);
      const exitTime  = new Date().toISOString();
      const durMins   = loginTime
        ? Math.round((Date.parse(exitTime) - Date.parse(loginTime)) / 60000)
        : null;
      gs.set(GK.EXIT_TIME, exitTime);
      gs.set(GK.TENANT_LOGIN_TIME, loginTime || '');
      if (durMins !== null) gs.set(GK.SESSION_DURATION_MINS, durMins);
      gs.clear();
    } catch (_) { /* silent */ }

    setGlobalContextData(null);
    setIsAuthenticated(false);
  }, []);

  return {
    isAuthenticated,
    globalContextData,
    setGlobalContextData,
    login,
    logout,
  };
}
